import {FanNumberSequence, MultiFanCoordinates, MultiFanSituationInputParameters} from "./multiFanNoise";
import {Observer} from "./Observer";
import {Cooler} from "./Cooler";
import {RequiredVisibilityRect} from "./RequiredVisibilityRect";
import {MultiFanNoiseResults} from "./multiFanNoiseCalculations";

/**
 * Główny agregat root - reprezentuje całą sytuację z wieloma chłodnicami
 */
export class Situation {
    private coolers: Cooler[] = [];
    private observer: Observer;
    private fanNumberSequence: FanNumberSequence;
    readonly additionalUserGivenFanPwlDba: number;

    constructor(
        coolers: Cooler[],
        observer: Observer,
        fanNumberSequence: FanNumberSequence,
        additionalUserGivenFanPwlDba: number
    ) {
        this.coolers = coolers;
        this.observer = observer;
        this.fanNumberSequence = fanNumberSequence;
        this.additionalUserGivenFanPwlDba = additionalUserGivenFanPwlDba;

        // Tworzenie wentylatorów dla każdej chłodnicy
        for (const cooler of this.coolers) {
            cooler.createFans();
        }
    }

    /**
     * Fabryka tworząca sytuację z parametrów wejściowych
     */
    static fromInputParameters(params: MultiFanSituationInputParameters): Situation {
        const fanNumberSequence = new FanNumberSequence();
        const coolers: Cooler[] = [];

        // Obliczenie pozycji każdej chłodnicy w macierzy
        const totalWidth = params.totalCoolerColumnCount * params.coolerWidthMeters +
            (params.totalCoolerColumnCount - 1) * params.xDistanceBetweenCoolers;
        const totalHeight = params.totalCoolerRowCount * params.coolerHeightMeters +
            (params.totalCoolerRowCount - 1) * params.yDistanceBetweenCoolers;

        // Zakotwiczenie na punkt (0,0) pośrodku
        const startX = -totalWidth / 2;
        const startY = -totalHeight / 2;

        for (let row = 0; row < params.totalCoolerRowCount; row++) {
            for (let col = 0; col < params.totalCoolerColumnCount; col++) {
                // Obliczenie pozycji środka chłodnicy
                const x = startX + col * (params.coolerWidthMeters + params.xDistanceBetweenCoolers) +
                    params.coolerWidthMeters / 2;
                const y = startY + row * (params.coolerHeightMeters + params.yDistanceBetweenCoolers) +
                    params.coolerHeightMeters / 2;

                const cooler = new Cooler(
                    new MultiFanCoordinates(x, y, params.coolerZCoordinate),
                    params.coolerWidthMeters,
                    params.coolerHeightMeters,
                    params.fanCaseDiameterMeters || 0.1,
                    params.fanColumnCountInCooler,
                    params.fanRowCountInCooler,
                    params.fanPwlDba,
                    params.coolerZCoordinate,
                    fanNumberSequence
                );
                coolers.push(cooler);
            }
        }

        const observer = new Observer(MultiFanCoordinates.fromObject(params.observerPosition));

        return new Situation(coolers, observer, fanNumberSequence, params.additionalUserGivenFanPwlDba);
    }

    getCoolers(): Cooler[] {
        return this.coolers;
    }

    getObserver(): Observer {
        return this.observer;
    }

    getCoolerWidthMeters(): number {
        return this.coolers[0]?.widthMeters ?? 0;
    }

    getCoolerHeightMeters(): number {
        return this.coolers[0]?.heightMeters ?? 0;
    }

    getFanPwlDba(): number {
        return this.coolers[0]?.pwlDba ?? 0;
    }

    /**
     * Aktualizuje obliczone wartości SPL dla wszystkich wentylatorów na podstawie wyników obliczeń
     */
    updateCalculationResults(results: MultiFanNoiseResults): void {
        // Tworzymy mapę wyników dla szybkiego dostępu po fanNumber
        const resultsMap = new Map<number, number>();
        for (const fanResult of results.fanResults) {
            resultsMap.set(fanResult.fanNumber, fanResult.splDba);
        }

        // Przechodzimy przez wszystkie chłodnice i wentylatory
        for (const cooler of this.coolers) {
            for (const fan of cooler.getFans()) {
                const splValue = resultsMap.get(fan.fanNumber);
                if (splValue !== undefined) {
                    fan.setObservedSpl(splValue);
                }
            }
        }
    }

    getRequiredVisibilityRect(): RequiredVisibilityRect {
        const rects: RequiredVisibilityRect[] = [
            ...this.coolers.map(c => c.getRequiredVisibilityRect()),
            this.observer.getRequiredVisibilityRect()
        ];
        return RequiredVisibilityRect.merge(...rects);
    }

    /**
     * Generuje SVG z automatycznym skalowaniem do wymiarów 800x800
     */
    renderToSVG(): string {
        const svgWidth = 1200;
        const svgHeight = 800;

        // Obliczenie wymaganych granic
        const visibilityRect = this.getRequiredVisibilityRect();

        // Obliczenie skali i przesunięcia
        const padding = 50;
        const availableWidth = svgWidth - 2 * padding;
        const availableHeight = svgHeight - 2 * padding;

        const scaleX = availableWidth / visibilityRect.width;
        const scaleY = availableHeight / visibilityRect.height;
        const scale = Math.min(scaleX, scaleY);

        // Offset do wycentrowania
        const totalScaledWidth = visibilityRect.width * scale;
        const totalScaledHeight = visibilityRect.height * scale;
        const offsetX = (svgWidth - totalScaledWidth) / 2 - visibilityRect.x1 * scale;
        const offsetY = (svgHeight - totalScaledHeight) / 2 - visibilityRect.y1 * scale;

        // Siatka współrzędnych
        const gridHtml = this.getGridSVG(scale);

        // Chłodnice - przekazujemy skalę
        const coolersHtml = this.coolers.map(cooler => cooler.renderToSVG(scale)).join('');

        // Obserwator - przekazujemy skalę
        const observerHtml = this.observer.renderToSVG(scale);

        return `
            <svg width="${svgWidth}" height="${svgHeight}" style="background: white;">
                <g transform="translate(${offsetX}, ${offsetY})">
                    ${gridHtml}
                    ${coolersHtml}
                    ${observerHtml}
                </g>
            </svg>
        `;
    }

    private getGridSVG(scale: number): string {
        const axisLine = 200;
        const strokeWidth = Math.max(0.05 * scale, 0.1); // Grubość osi
        const originRadius =0.1*scale;   // Radius punktu origin

        return `
            <g>
                
                <line x1="${-axisLine}" y1="0" x2="${axisLine}" y2="0" stroke="#aaa" stroke-width="${strokeWidth}" stroke-dasharray="2,2" />
             
                <line x1="0" y1="${-axisLine}" x2="0" y2="${axisLine}" stroke="#aaa" stroke-width="${strokeWidth}" stroke-dasharray="2,2" />
               
                <circle cx="0" cy="0" r="${originRadius}" fill="#ff0000" />
            </g>
        `;
    }
}